import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { AuthCard } from "@/components/auth/AuthCard";

export default function MfaPage() {
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showError = (message: string) => {
    setSuccess(null);
    setError(message);
  };

  const showSuccess = (message: string) => {
    setError(null);
    setSuccess(message);
    setSecret(null);
    setOtpauthUri(null);
    setSetupCode("");
    setDisablePassword("");
    setDisableCode("");
  };

  const handleSetup = async () => {
    setIsLoading(true);
    showError("");
    try {
      const result = await api.setupMfa();
      setSecret(result.secret);
      setOtpauthUri(result.otpauthUri);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Setup failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secret) return;
    setIsLoading(true);
    showError("");
    try {
      await api.enableMfa(setupCode);
      showSuccess("Multi-factor authentication enabled.");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Enable failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    showError("");
    try {
      await api.disableMfa(disablePassword, disableCode);
      showSuccess("Multi-factor authentication disabled.");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Disable failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCard
      title="EVzone Media & Content Platform"
      subtitle="Multi-factor authentication"
      footer={<Link to="/media-content/command-center">Back to Command Center</Link>}
    >
      <div className="ev-login__form" style={{ gap: 24 }}>
        {error && <div className="ev-login__error" role="alert">{error}</div>}
        {success && <div className="ev-login__success" role="status">{success}</div>}

        {!secret && !success && (
          <div>
            <p style={{ margin: "0 0 12px", color: "#b4b4b4" }}>
              Use an authenticator app such as Google Authenticator, Authy, or 1Password to generate time-based codes.
            </p>
            <button type="button" className="ev-login__submit" onClick={handleSetup} disabled={isLoading}>
              {isLoading ? "Working…" : "Set up MFA"}
            </button>
          </div>
        )}

        {secret && (
          <form onSubmit={handleEnable} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <p style={{ margin: "0 0 8px", color: "#b4b4b4" }}>Scan or enter this secret in your authenticator app:</p>
              <code
                style={{
                  display: "block",
                  padding: 12,
                  background: "#141414",
                  border: "1px solid #2a2a2a",
                  borderRadius: 6,
                  color: "#ff6b00",
                  wordBreak: "break-all",
                  fontSize: 14,
                }}
              >
                {secret}
              </code>
              {otpauthUri && (
                <a
                  href={otpauthUri}
                  style={{ display: "inline-block", marginTop: 8, color: "#ff6b00", fontSize: 13 }}
                >
                  Open in authenticator app
                </a>
              )}
            </div>
            <label className="ev-login__field">
              <span>Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value)}
                disabled={isLoading}
                required
                maxLength={6}
                pattern="[0-9]{6}"
              />
            </label>
            <button type="submit" className="ev-login__submit" disabled={isLoading}>
              {isLoading ? "Enabling…" : "Enable MFA"}
            </button>
            <button
              type="button"
              className="ev-login__secondary"
              onClick={() => { setSecret(null); setOtpauthUri(null); setSetupCode(""); }}
              disabled={isLoading}
            >
              Cancel setup
            </button>
          </form>
        )}

        <hr style={{ border: "none", borderTop: "1px solid #2a2a2a", margin: "8px 0" }} />

        <form onSubmit={handleDisable} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <p style={{ margin: 0, color: "#b4b4b4", fontSize: 14 }}>To disable MFA, confirm your password and current authenticator code.</p>
          <label className="ev-login__field">
            <span>Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              disabled={isLoading}
            />
          </label>
          <label className="ev-login__field">
            <span>Authenticator code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              disabled={isLoading}
              maxLength={6}
              pattern="[0-9]{6}"
            />
          </label>
          <button type="submit" className="ev-login__submit ev-login__submit--secondary" disabled={isLoading}>
            {isLoading ? "Disabling…" : "Disable MFA"}
          </button>
        </form>
      </div>
    </AuthCard>
  );
}
